import * as React from "react";
import { 
  Button, 
  Text, 
  Badge,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Tooltip
} from "@fluentui/react-components";
import { 
  MoreHorizontal24Regular,
  Edit24Regular,
  Delete24Regular,
  Chat24Regular
} from "@fluentui/react-icons";
import { useTranslation } from 'react-i18next';

interface ChatMenuItem {
  id: string;
  label: string;
  icon: React.ReactElement;
  onClick: (chat: any) => void;
  disabled?: boolean;
}

interface ChatNavButtonProps {
  chat: any; // Your chat object
  label: string;
  isActive?: boolean;
  onClick: () => void;
  onRename: (chat: any) => void;
  onDelete: (chat: any) => void;
  showBadge?: boolean;
  additionalMenuItems?: ChatMenuItem[];
  icon?: React.ReactElement;
}

const ChatNavButton: React.FC<ChatNavButtonProps> = ({ 
  chat,
  label,  
  onClick, 
  isActive = false,
  onRename,
  onDelete,
  showBadge = true,
  additionalMenuItems = [],
  icon = <Chat24Regular />
}) => {
  const { t } = useTranslation();
  const activeBadgeText = t('common.active', 'Active');
  const moreOptionsText = t('chat.more', 'More');

  return (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: 0,
      width: '100%',
      position: 'relative'
    }}>
      {/* Main chat button */}
      <Button
        appearance={isActive ? "primary" : "subtle"}
        icon={icon}
        onClick={onClick}
        style={{ 
          justifyContent: 'flex-start',
          width: '100%',
          textAlign: 'left',
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
          flex: 1
        }}
        size="large"
      >
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          width: '100%',
          alignItems: 'center',
          minWidth: 0 // Allow text truncation
        }}>
          <Text 
            style={{ 
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {label}
          </Text>
          {isActive && showBadge && (
            <Badge 
              appearance="filled" 
              color="brand" 
              size="small"
              style={{ marginLeft: 8, flexShrink: 0 }}
            >
              {activeBadgeText}
            </Badge>
          )}
        </div>
      </Button>

      {/* Menu button attached to main button */}
      <Menu positioning="below-end">
        <MenuTrigger disableButtonEnhancement>
          <Tooltip content={moreOptionsText} relationship="label">
            <Button 
              appearance={isActive ? "primary" : "subtle"}
              icon={<MoreHorizontal24Regular />}
              aria-label={moreOptionsText}
              style={{
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
                padding: '0 8px',
                height: "100%",
                minWidth: 'auto'
              }}
              size="large"
            />
          </Tooltip>
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            <MenuItem 
              icon={<Edit24Regular />} 
              onClick={() => onRename(chat)}
            >
              {t('chat.rename', 'Rename')}
            </MenuItem>
            <MenuItem 
              icon={<Delete24Regular />} 
              onClick={() => onDelete(chat)}
            >
              {t('chat.delete', 'Delete')}
            </MenuItem>
            {additionalMenuItems.map((item) => (
              <MenuItem 
                key={item.id}
                icon={item.icon}
                onClick={() => item.onClick(chat)}
                disabled={item.disabled}
              >
                {item.label}
              </MenuItem>
            ))}
          </MenuList>
        </MenuPopover>
      </Menu>
    </div>
  );
};
export default ChatNavButton;